import { isEqual } from "lodash";
import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";

import { useBulkEditFormContext } from "@ryde/redux";
import {
  BulkMultiselectActionsEnum,
  getBulkFormFieldName,
  getBulkMultiselectAssignedValuesUnion,
  getBulkMultiselectPrefillForAction,
} from "@ryde/shared";

/** Chips must follow the bulk action, not the first selected row.
 * ADD/REPLACE start empty so existing ids are not written to every row by accident.
 * REMOVE prefills the union of the field across selected loaded rows;
 * the user can then trim chips. Switching action rewrites the field.
 */
export const useClearBulkMultiselectOnAction = (fieldName: string): void => {
  const { watch, getValues, setValue } = useFormContext();
  const { selectedRowsValues } = useBulkEditFormContext();
  const bulkAction = watch(getBulkFormFieldName(fieldName));
  const previousActionRef = useRef(bulkAction);

  useEffect(() => {
    const previousAction = previousActionRef.current;
    previousActionRef.current = bulkAction;

    if (previousAction === bulkAction) {
      return;
    }

    const nextValue = getBulkMultiselectPrefillForAction({
      bulkAction,
      assignedValuesUnion:
        bulkAction === BulkMultiselectActionsEnum.REMOVE
          ? getBulkMultiselectAssignedValuesUnion({
              selectedRowsValues,
              fieldName,
            })
          : [],
    });

    if (nextValue === undefined) {
      return;
    }

    if (isEqual(getValues(fieldName), nextValue)) {
      return;
    }

    setValue(fieldName, nextValue, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [bulkAction, fieldName, getValues, selectedRowsValues, setValue]);
};
